import React, { useEffect, useState } from 'react';
import { Form, Radio, Space, Button } from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { Icon } from '@/src/tools/icon';
import { SSLMODE, EnumStatus } from '@/src/types/common/domain';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { Modal, ModalFooter } from '@/src/components/Antd';
import css from '../css.module.less';

const { Item } = Form;

interface SSLCertificateMethodProps {
  form: any;
  domain: string;
  sslMode?: SSLMODE;
  domainStatus: string;
  confirmSSLMode: (sslMode: string) => Promise<any>;
}

const SSLCertificateMethod: React.FC<SSLCertificateMethodProps> = ({
  form,
  domain,
  sslMode,
  domainStatus,
  confirmSSLMode,
}) => {
  const i18n = useI18n();
  const getI18n = (key: string) => i18nGet(i18n, key);

  const [fold, setFold] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMode, setSelectedMode] = useState<SSLMODE>(
    sslMode ?? SSLMODE.ACME,
  );
  const [modalState, setModalState] = useState<{
    visible: boolean;
    mode?: SSLMODE;
  }>({
    visible: false,
  });
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const deletingStatus = domainStatus === EnumStatus.Deleting;

  // 同步表单状态
  useEffect(() => {
    const initial = sslMode ?? SSLMODE.ACME;
    form.setFieldsValue({ sslMode: initial });
    setSelectedMode(initial);
  }, [sslMode]);

  const handleChange = (e: RadioChangeEvent) => {
    const next = e.target.value;
    setSelectedMode(next);
  };

  const handleUpdate = () => {
    //选择同一个Mode，不弹窗
    if (selectedMode === sslMode) {
      setEditMode(false);
    } else {
      setModalState({ visible: true, mode: selectedMode });
    }
  };

  //首次提交不弹窗
  const handleFirstSubmit = async () => {
    setSelectLoading(true);
    if (!selectedMode) return;
    try {
      await confirmSSLMode(selectedMode);
      form.setFieldsValue({ sslMode: selectedMode });
    } finally {
      setSelectLoading(false);
      setEditMode(false);
    }
  };

  //用于update按钮进入可编辑状态 UXPSA-14155
  const handleEditButton = () => {
    setEditMode(true);
  };

  const handleConfirmChange = async () => {
    setSelectLoading(true);
    if (!modalState.mode) return;
    try {
      await confirmSSLMode(modalState.mode);
      form.setFieldsValue({ sslMode: modalState.mode });
    } finally {
      setSelectLoading(false);
      setModalState({ visible: false });
      setEditMode(false);
    }
  };

  const handleCancelChange = () => setModalState({ visible: false });

  const getConfirmText = (mode?: SSLMODE) => {
    if (mode === SSLMODE.ACME)
      return <>{getI18n('salesChannelDomainAndStatus.edit.turnToACME')}</>;
    if (mode === SSLMODE.BYO)
      return <>{getI18n('salesChannelDomainAndStatus.edit.turnToBYO')}</>;
    return null;
  };

  const radioOptions = [
    {
      value: SSLMODE.ACME,
      label: `${getI18n('salesChannelDomainAndStatus.edit.optionACME')}`,
    },
    {
      value: SSLMODE.BYO,
      label: `${getI18n('salesChannelDomainAndStatus.edit.optionBYO')}`,
    },
  ];

  return (
    <>
      <Item name='sslMode' initialValue={sslMode ?? SSLMODE.ACME} hidden />

      <SectionWrapper
        titleClassName={css.titleContainer}
        containerClassName={css.containerClassName}
        titleOnClick={() => setFold(!fold)}
        rightPart={<Icon type={fold ? 'icondown1' : 'iconright'} />}
        hideContent={!fold}
        title={getI18n('salesChannelDomainAndStatus.edit.sSLCertificateTitle')}>
        <Item className={css.certificateItem}>
          <Radio.Group
            value={selectedMode}
            onChange={handleChange}
            disabled={!domain || (!editMode && !!sslMode) || selectLoading}
            className={sslMode ? css.radioGroup : ''}>
            <Space direction='vertical' className={css.space}>
              {radioOptions.map((opt) => (
                <div className={css.item} key={opt.value}>
                  <Radio value={opt.value}>{opt.label}</Radio>
                </div>
              ))}
            </Space>
          </Radio.Group>
          {editMode ? (
            <Button
              type='primary'
              onClick={handleUpdate}
              disabled={!domain || deletingStatus}
              className={css.updateSslMode}>
              {'Update'}
            </Button>
          ) : (
            <Button
              type={sslMode ? 'default' : 'primary'}
              onClick={sslMode ? handleEditButton : handleFirstSubmit}
              disabled={selectLoading || !domain || deletingStatus}
              loading={selectLoading}
              className={`${css.updateSslMode} ${
                sslMode ? css.updateNoEditable : ''
              }`}>
              {sslMode ? 'Update' : 'Submit'}
            </Button>
          )}
        </Item>
      </SectionWrapper>

      <Modal
        width={600}
        visible={modalState.visible}
        onCancel={handleCancelChange}
        title='Reminder'>
        <div className={css.verifyModal}>{getConfirmText(modalState.mode)}</div>
        <ModalFooter
          onCancel={handleCancelChange}
          okText={getI18n('general.antd.Modal.ModalFooter.confirm')}
          onOk={handleConfirmChange}
          loading={selectLoading}
        />
      </Modal>
    </>
  );
};

export default SSLCertificateMethod;
